import type { RawDraftContentState } from 'draft-js';
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  convertFromRaw,
} from 'draft-js';
import { isJSON } from './is-json';
import { HTML_BLOCK_ELEMENT_REGEX } from '@/regexes';

const plainTextToHtml = (value: string): string => {
  const normalized = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return normalized
    .split('\n')
    .map(line => `<p>${line || '<br>'}</p>`)
    .join('');
};

const stringToDraftJSContentState = (value: string) => {
  const htmlValue = HTML_BLOCK_ELEMENT_REGEX.test(value)
    ? value
    : plainTextToHtml(value);
  const blocksFromHTML = convertFromHTML(htmlValue);
  return ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap,
  );
};

export const stringToDraftJSContentStateString = (value: string) => {
  const raw: RawDraftContentState = convertToRaw(
    stringToDraftJSContentState(value),
  );

  return JSON.stringify(raw);
};

export const getMentionedUserIdsFromRaw = (
  raw: RawDraftContentState,
): number[] => {
  const ids = Object.values(raw.entityMap ?? {})
    .filter(entity => entity?.type === 'mention')
    .map(entity => Number(entity.data?.mention?.id))
    .filter(id => Number.isFinite(id));

  return Array.from(new Set(ids));
};

export const getMentionedUserIdsFromDraftJs = (
  value: string | null,
): number[] => {
  if (!value || !isJSON(value)) return [];

  try {
    const parsed = JSON.parse(value) as RawDraftContentState;
    if (!parsed || !parsed.entityMap) return [];
    return getMentionedUserIdsFromRaw(parsed);
  } catch {
    return [];
  }
};

export const draftJsRawToPlainText = (value: string): string => {
  if (!value) return '';

  if (!isJSON(value)) return value;

  try {
    const parsed = JSON.parse(value) as RawDraftContentState;
    if (!parsed || !Array.isArray(parsed.blocks)) return value;
    const contentState = convertFromRaw(parsed);
    return contentState.getPlainText('\n');
  } catch {
    return value;
  }
};
